import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { MapPinIcon, TrashIcon } from '@heroicons/react/20/solid';
import { PinMap } from './PinMap';
const sortMarkers = (markers) => {
    return markers.sort((a, b) => {
        // Sort by latitude first
        if (a.lngLat[1] < b.lngLat[1])
            return -1;
        if (a.lngLat[1] > b.lngLat[1])
            return 1;
        // If latitudes are equal, sort by longitude
        if (a.lngLat[0] < b.lngLat[0])
            return -1;
        if (a.lngLat[0] > b.lngLat[0])
            return 1;
        return 0; // If both latitudes and longitudes are equal
    });
};
const generateRandomLngLat = (lowerLngLat, upperLngLat) => {
    const [lowerLng, lowerLat] = lowerLngLat;
    const [upperLng, upperLat] = upperLngLat;
    const lngRange = upperLng - lowerLng;
    const latRange = upperLat - lowerLat;
    const randomLng = lowerLng + Math.random() * lngRange;
    const randomLat = lowerLat + Math.random() * latRange;
    return [randomLng, randomLat];
};
// Queens
const lngLatBounds1 = [
    [-73.9626, 40.5417], // Lower left corner
    [-73.6995, 40.8007], // Upper right corner
];
// Manhattan
const lngLatBounds2 = [
    [-74.0479, 40.6829], // Lower left corner
    [-73.9067, 40.882], // Upper right corner
];
// Brooklyn
const lngLatBounds3 = [
    [-74.0419, 40.5704], // Lower left corner
    [-73.8334, 40.7395], // Upper right corner
];
const markerSet1 = {
    id: '1',
    name: 'NYC Landmarks',
    markers: sortMarkers(Array.from({ length: 50 }, (_, i) => ({
        id: `landmark-${i + 1}`,
        lngLat: generateRandomLngLat(lngLatBounds1[0], lngLatBounds1[1]),
        title: `Landmark ${i + 1}`,
        content: _jsxs("p", { children: ["This is Landmark ", i + 1, " in NYC."] }),
    }))),
    icon: _jsx(MapPinIcon, {}), // Replace with your React component
    color: '#4285F4',
    isVisible: true,
};
const markerSet2 = {
    id: '2',
    name: 'NYC Parks',
    markers: sortMarkers(Array.from({ length: 50 }, (_, i) => ({
        id: `park-${i + 1}`,
        lngLat: generateRandomLngLat(lngLatBounds2[0], lngLatBounds2[1]),
        title: `Park ${i + 1}`,
        content: `This is Park ${i + 1} in NYC.`,
    }))),
    icon: _jsx(MapPinIcon, {}), // Replace with your React component
    color: '#34A853',
    isVisible: true,
    isConnected: true,
};
const markerSet3 = {
    id: '3',
    name: 'NYC Museums',
    markers: sortMarkers(Array.from({ length: 50 }, (_, i) => ({
        id: `museum-${i + 1}`,
        lngLat: generateRandomLngLat(lngLatBounds3[0], lngLatBounds3[1]),
        title: `Museum ${i + 1}`,
        content: (_jsxs("div", { children: [_jsx("h5", { className: "text-red-500", children: "Museums" }), _jsxs("p", { children: ["This is Museum ", i + 1, " in NYC."] })] })),
    }))),
    icon: _jsx(TrashIcon, {}), // Replace with your React component
    color: '#FBBC05',
    lineColor: 'black',
    isVisible: true,
};
const testSets = [markerSet1, markerSet2, markerSet3];
export const TestPinMap = () => {
    const [routes, setRoutes] = useState(testSets);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [isClustered, setIsClustered] = useState(false);
    // Handler for toggling connection
    const handleToggleConnection = (id) => {
        setRoutes(routes.map((route) => (route.id === id ? { ...route, isConnected: !route.isConnected } : route)));
    };
    // Handler for toggling visibility
    const handleToggleVisibility = (id) => {
        setRoutes(routes.map((route) => (route.id === id ? { ...route, isVisible: !route.isVisible } : route)));
    };
    // Handler for marker click
    const handleMarkerClick = (marker) => {
        const markerRoute = routes.find((route) => route.markers.some((m) => m.id === marker.id));
        if (markerRoute?.isVisible) {
            const newRoutes = [...routes];
            if (selectedMarker) {
                const prevMarkerRouteIndex = newRoutes.findIndex((route) => route.markers.some((m) => m.id === selectedMarker.id));
                if (prevMarkerRouteIndex !== -1) {
                    newRoutes[prevMarkerRouteIndex] = {
                        ...newRoutes[prevMarkerRouteIndex],
                        markers: newRoutes[prevMarkerRouteIndex].markers.map((m) => m.id === selectedMarker.id ? { ...selectedMarker } : m),
                    };
                }
            }
            if (marker.id === selectedMarker?.id) {
                setRoutes(newRoutes);
                setSelectedMarker(null);
                return;
            }
            // Update the color of the clicked marker
            const markerRouteIndex = newRoutes.findIndex((route) => route.markers.some((m) => m.id === marker.id));
            if (markerRouteIndex !== -1) {
                newRoutes[markerRouteIndex] = {
                    ...newRoutes[markerRouteIndex],
                    markers: newRoutes[markerRouteIndex].markers.map((m) => (m.id === marker.id ? { ...m, overrideColor: 'red' } : m)),
                };
            }
            // Set the new routes array
            setRoutes(newRoutes);
            // Update the selected marker
            setSelectedMarker(marker);
        }
    };
    // Handler for route click
    const handleRouteClick = (route) => {
        if (selectedRoute?.id === route.id) {
            setSelectedRoute(null);
        }
        else {
            if (selectedMarker)
                handleMarkerClick(selectedMarker);
            setSelectedRoute(route);
        }
    };
    const findCenter = (markerSets) => {
        let totalLat = 0;
        let totalLng = 0;
        let count = 0;
        markerSets.forEach((markerSet) => {
            markerSet.markers.forEach((marker) => {
                totalLng += marker.lngLat[0];
                totalLat += marker.lngLat[1];
                count++;
            });
        });
        return [totalLng / count, totalLat / count];
    };
    return (_jsxs("div", { className: "flex h-[55vh]", children: [_jsxs("div", { className: "h-full w-1/3 overflow-y-auto bg-gray-100 p-4", children: [routes.map((route) => (_jsx("div", { className: `mb-4 cursor-pointer rounded-lg shadow-md ${selectedRoute?.id === route.id ? 'bg-blue-200' : 'bg-white'}`, onClick: () => handleRouteClick(route), children: _jsxs("div", { className: "p-4", children: [_jsx("h2", { className: "mb-2 text-xl font-bold", children: route.name }), _jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: route.isVisible, onChange: () => handleToggleVisibility(route.id) }), ' ', "Visible"] }), _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: route.isConnected || false, disabled: !route.isVisible, onChange: () => handleToggleConnection(route.id) }), ' ', "Connected"] })] }, `${route.id}-controls`)] }) }, route.id))), _jsx("div", { className: "flex w-full items-center justify-center p-4", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: isClustered, onChange: () => setIsClustered(!isClustered) }), "Clustering"] }) })] }), _jsxs("div", { className: "relative h-full w-2/3", children: [_jsx("div", { className: "h-full w-full bg-gray-100 pb-10", children: _jsx(PinMap, { markerSets: routes, center: findCenter(routes), selectedMarker: selectedMarker, selectedMarkerSet: selectedRoute, onMarkerClick: handleMarkerClick }) }), selectedRoute && (_jsxs("div", { className: "absolute right-0 top-0 h-2/3 w-64 overflow-y-auto bg-gray-100 bg-opacity-90 p-4", children: [_jsx("h3", { className: "mb-2 text-lg font-bold", children: "Markers" }), _jsx("hr", { className: "h-2" }), _jsx("div", { className: "h-3/4 overflow-y-auto", children: _jsx("ul", { children: selectedRoute.markers.map((marker) => (_jsx("li", { children: _jsx("button", { className: "w-full text-left text-blue-500 hover:text-blue-700", onClick: () => handleMarkerClick(marker), children: marker.title }) }, marker.id))) }) })] })), _jsx("div", { className: "text-semibold absolute bottom-0 left-0 right-0 bg-gray-100 bg-opacity-90 p-2 text-center", children: selectedMarker?.title })] })] }));
};
//# sourceMappingURL=TestPinMap.js.map